import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../services/api';
import { PoS, Visit } from '../types';
import {
  ArrowLeft, MapPin, Phone, Mail, User, Building, Calendar,
  Package, Edit2, ExternalLink, CheckCircle, Clock, AlertCircle, CalendarPlus, Star
} from 'lucide-react';
import { VisitDetailModal } from '../components/visits/VisitDetailModal';
import { ScheduleFollowupModal } from '../components/visits/ScheduleFollowupModal';
import { PosNotesSection } from '../components/pos/PosNotesSection';
import { formatDate } from '../utils/datetime';

const PoSDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pos, setPos] = useState<PoS | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [distributors, setDistributors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showScheduleFollowup, setShowScheduleFollowup] = useState(false);
  const [hasPendingFollowup, setHasPendingFollowup] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPoSDetails();
      fetchPoSVisits();
      fetchDistributors();
      checkPendingFollowup();
    }
  }, [id]);

  const checkPendingFollowup = async () => {
    try {
      const res = await api.get(`/followups/pos/${id}`);
      setHasPendingFollowup(!!res.data);
    } catch (err) {
      setHasPendingFollowup(false);
    }
  };

  const togglePremium = async () => {
    if (!pos) return;
    try {
      const res = await api.put(`/pos/${pos.id}`, { is_premium: !pos.is_premium });
      setPos(res.data);
      toast.success(res.data.is_premium ? 'Marked as premium' : 'Premium status removed');
    } catch (err) {
      console.error('Failed to update premium status:', err);
      toast.error('Failed to update premium status');
    }
  };

  const fetchPoSDetails = async () => {
    try {
      const res = await api.get(`/pos/${id}`);
      setPos(res.data);
    } catch (err) {
      console.error('Failed to fetch PoS details:', err);
      toast.error('Failed to load PoS details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPoSVisits = async () => {
    try {
      const res = await api.get(`/visits?pos_id=${id}`);
      setVisits(res.data.data || res.data || []);
    } catch (err) {
      console.error('Failed to fetch PoS visits:', err);
    }
  };

  const fetchDistributors = async () => {
    try {
      const res = await api.get(`/pos/${id}/distributors`);
      setDistributors(res.data || []);
    } catch (err) {
      console.error('Failed to fetch distributors:', err);
    }
  };

  const handleVisitClick = async (visitId: string) => {
    try {
      const res = await api.get(`/visits/${visitId}`);
      setSelectedVisit(res.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Failed to fetch visit details:', err);
      toast.error('Failed to load visit details');
    }
  };

  const groupVisitsByType = (visits: Visit[]) => {
    return {
      first_time: visits.filter(v => v.visit_type === 'first_time'),
      revisit: visits.filter(v => v.visit_type !== 'first_time'),
    };
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="bg-white rounded-2xl p-6 space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!pos) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-2xl p-12 text-center">
          <MapPin size={64} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">PoS Not Found</h2>
          <button
            onClick={() => navigate('/pos')}
            className="mt-4 px-6 py-3 bg-brand-600 text-white rounded-xl font-bold hover:bg-brand-700"
          >
            Back to PoS List
          </button>
        </div>
      </div>
    );
  }

  const groupedVisits = groupVisitsByType(visits);
  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'placement_secured': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'follow_up_needed': return 'bg-yellow-100 text-yellow-800';
      case 'competitor_exclusive': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pos')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-3xl font-black text-gray-900 truncate">{pos.name}</h1>
              {pos.is_premium && (
                <Star size={20} className="text-amber-400 fill-amber-400 flex-shrink-0" aria-label="Premium location" />
              )}
            </div>
            <p className="text-xs md:text-sm text-gray-500">Point of Sale Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={togglePremium}
            title={pos.is_premium ? 'Remove premium status' : 'Mark as premium'}
            className={`flex-shrink-0 p-3 rounded-xl font-bold transition-colors shadow-md inline-flex items-center justify-center gap-2 text-sm border-2 ${
              pos.is_premium
                ? 'bg-amber-50 border-amber-300 text-amber-600 hover:bg-amber-100'
                : 'bg-white border-gray-200 text-gray-400 hover:border-amber-300 hover:text-amber-500'
            }`}
          >
            <Star size={18} className={pos.is_premium ? 'fill-amber-400 text-amber-400' : ''} />
          </button>
          <button
            onClick={() => setShowScheduleFollowup(true)}
            className={`flex-1 md:flex-none px-3 md:px-5 py-3 rounded-xl font-bold transition-colors shadow-md inline-flex items-center justify-center gap-2 text-sm ${
              hasPendingFollowup
                ? 'bg-orange-100 text-orange-700 border-2 border-orange-300 hover:bg-orange-200'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <CalendarPlus size={16} />
            <span className="hidden md:inline">{hasPendingFollowup ? 'Add Follow-up' : 'Schedule Follow-up'}</span>
            <span className="md:hidden">Follow-up</span>
          </button>
          <Link
            to={`/pos/${pos.id}/edit`}
            className="flex-1 md:flex-none px-3 md:px-6 py-3 bg-brand-600 text-white rounded-xl font-bold hover:bg-brand-700 transition-colors shadow-md inline-flex items-center justify-center gap-2 text-sm"
          >
            <Edit2 size={16} />
            <span className="hidden md:inline">Edit PoS</span>
            <span className="md:hidden">Edit</span>
          </Link>
          <Link
            to={`/orders?new=1&posId=${pos.id}`}
            className="flex-1 md:flex-none px-3 md:px-6 py-3 bg-white text-brand-700 border-2 border-brand-200 rounded-xl font-bold hover:bg-brand-50 transition-colors shadow-md inline-flex items-center justify-center gap-2 text-sm"
          >
            <Package size={16} />
            <span className="hidden md:inline">New order</span>
            <span className="md:hidden">Order</span>
          </Link>
        </div>
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Store Information Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6 space-y-4">
          <h2 className="text-lg md:text-xl font-black text-gray-900 mb-3">Store Information</h2>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Store Type</label>
              <p className="text-sm font-bold text-gray-900 capitalize mt-1">{pos.store_type?.replace(/_/g, ' ')}</p>
            </div>
            
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Store ID</label>
              <p className="text-sm font-mono font-bold text-brand-600 mt-1">{pos.store_id || 'N/A'}</p>
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Address</label>
              <div className="flex items-start gap-2 mt-1">
                <MapPin size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p>{pos.street} {pos.street_number}</p>
                  <p>{pos.postal_code} {pos.city}</p>
                  {pos.district && <p className="text-gray-500">District: {pos.district}</p>}
                  <p>{pos.country || 'Germany'}</p>
                </div>
              </div>
              {pos.latitude && pos.longitude && (
                <a
                  href={`https://www.google.com/maps?q=${pos.latitude},${pos.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2"
                >
                  <ExternalLink size={12} />
                  Open in Google Maps
                </a>
              )}
            </div>

            {pos.estimated_traffic && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Estimated Traffic</label>
                <p className="text-sm text-gray-900 capitalize mt-1">{pos.estimated_traffic}</p>
              </div>
            )}

            {pos.target_audience && pos.target_audience.length > 0 && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Target Audience</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {pos.target_audience.map((audience: string, index: number) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {audience}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(pos.tax_number || pos.vat_id) && (
              <div className="col-span-full border-t border-gray-100 pt-3">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Tax Information</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pos.tax_number && (
                    <div>
                      <label className="text-xs text-gray-500">Tax Number (Steuernummer)</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">{pos.tax_number}</p>
                    </div>
                  )}
                  {pos.vat_id && (
                    <div>
                      <label className="text-xs text-gray-500">VAT ID (USt-IdNr)</label>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">{pos.vat_id}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-black text-gray-900 mb-3 md:mb-4">Contact</h2>
          
          <div className="space-y-3">
            {/* General Store Contact (if different from contact person) */}
            {pos.phone && pos.phone !== pos.contact_person_phone && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Store Phone</label>
                <a 
                  href={`tel:${pos.phone}`}
                  className="flex items-center gap-2 mt-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Phone size={16} />
                  {pos.phone}
                </a>
              </div>
            )}

            {pos.email && pos.email !== pos.contact_person_email && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Store Email</label>
                <a 
                  href={`mailto:${pos.email}`}
                  className="flex items-center gap-2 mt-1 text-sm text-blue-600 hover:text-blue-800 break-all"
                >
                  <Mail size={16} className="flex-shrink-0" />
                  {pos.email}
                </a>
              </div>
            )}

            {pos.invoice_email && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Invoice Email</label>
                <a
                  href={`mailto:${pos.invoice_email}`}
                  className="flex items-center gap-2 mt-1 text-sm text-blue-600 hover:text-blue-800 break-all"
                >
                  <Mail size={16} className="flex-shrink-0" />
                  {pos.invoice_email}
                </a>
              </div>
            )}

            {pos.owner_name && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Owner (Inhaber)</label>
                <div className="flex items-center gap-2 mt-1">
                  <User size={16} className="text-gray-400" />
                  <p className="text-sm font-bold text-gray-900">{pos.owner_name}</p>
                </div>
              </div>
            )}

            {(pos.invoice_name ||
              pos.invoice_street ||
              pos.invoice_postal_code ||
              pos.invoice_city) && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Invoice Address
                </label>
                <div className="flex items-start gap-2 mt-1">
                  <MapPin size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    {pos.invoice_name && (
                      <p className="font-bold text-gray-900">{pos.invoice_name}</p>
                    )}
                    {(pos.invoice_street || pos.invoice_street_number) && (
                      <p>
                        {pos.invoice_street} {pos.invoice_street_number}
                      </p>
                    )}
                    {(pos.invoice_postal_code || pos.invoice_city) && (
                      <p>
                        {pos.invoice_postal_code} {pos.invoice_city}
                      </p>
                    )}
                    {pos.invoice_country && <p>{pos.invoice_country}</p>}
                  </div>
                </div>
              </div>
            )}

            {pos.contact_person_name && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Contact Person</label>
                <div className="flex items-center gap-2 mt-1">
                  <User size={16} className="text-gray-400" />
                  <div>
                    <p className="text-sm font-bold text-gray-900">{pos.contact_person_name}</p>
                    {pos.contact_person_position && (
                      <p className="text-xs text-gray-500">{pos.contact_person_position}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {pos.contact_person_phone && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Phone</label>
                <a 
                  href={`tel:${pos.contact_person_phone}`}
                  className="flex items-center gap-2 mt-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Phone size={16} />
                  {pos.contact_person_phone}
                </a>
              </div>
            )}

            {pos.contact_person_email && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Email</label>
                <a 
                  href={`mailto:${pos.contact_person_email}`}
                  className="flex items-center gap-2 mt-1 text-sm text-blue-600 hover:text-blue-800 break-all"
                >
                  <Mail size={16} className="flex-shrink-0" />
                  {pos.contact_person_email}
                </a>
              </div>
            )}

            {pos.is_decision_maker !== undefined && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Decision Maker</label>
                <p className="text-sm text-gray-900 mt-1">{pos.is_decision_maker ? 'Yes' : 'No'}</p>
              </div>
            )}

            {pos.contact_availability_time && (
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Best Time to Reach</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pos.contact_availability_time.split(',').filter(Boolean).map((time) => {
                    const timeMap: Record<string, { label: string; emoji: string }> = {
                      'morning': { label: 'Morning', emoji: '🌅' },
                      'afternoon': { label: 'Afternoon', emoji: '☀️' },
                      'evening': { label: 'Evening', emoji: '🌙' }
                    };
                    const timeInfo = timeMap[time.trim()];
                    return timeInfo ? (
                      <span 
                        key={time}
                        className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium flex items-center gap-1.5"
                      >
                        <span>{timeInfo.emoji}</span>
                        <span>{timeInfo.label}</span>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Distributors Card */}
        {distributors.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-black text-gray-900 mb-3 md:mb-4">Distributors ({distributors.length})</h2>
            
            <div className="space-y-3">
              {distributors.map((dist: any) => (
                <div 
                  key={dist.id} 
                  className={`p-3 rounded-lg border-2 ${
                    dist.is_primary 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-bold text-gray-900">{dist.distributor_name}</p>
                      {dist.pos_number_at_distributor && (
                        <p className="text-xs text-gray-600 mt-1">
                          Customer #: <span className="font-mono font-semibold">{dist.pos_number_at_distributor}</span>
                        </p>
                      )}
                    </div>
                    {dist.is_primary && (
                      <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded">
                        PRIMARY
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Visit History */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6">
        <div className="space-y-2 mb-4 md:mb-6">
          <h2 className="text-lg md:text-xl font-black text-gray-900">Visit History ({visits.length})</h2>
          {pos.last_visit_date && (
            <p className="text-xs md:text-sm text-gray-500">
              Last visited: {formatDate(pos.last_visit_date)}
            </p>
          )}
        </div>

        {visits.length === 0 ? (
          <div className="text-center py-12">
            <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">No visits recorded yet</p>
            <Link
              to="/visits/new"
              className="inline-block mt-4 px-6 py-3 bg-brand-600 text-white rounded-xl font-bold hover:bg-brand-700"
            >
              Create First Visit
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* First-Time Visits */}
            {groupedVisits.first_time.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-green-600 mb-3 flex items-center gap-2">
                  <Package size={20} />
                  First-Time Activations ({groupedVisits.first_time.length})
                </h3>
                <div className="space-y-2">
                  {groupedVisits.first_time.map((visit) => (
                    <div
                      key={visit.id}
                      onClick={() => handleVisitClick(visit.id)}
                      className="p-4 border border-gray-200 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Calendar size={14} className="text-gray-500" />
                            <span className="text-sm font-bold text-gray-900">
                              {formatDate(visit.created_at)}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-800">
                              🎯 FIRST
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getOutcomeColor(visit.visit_outcome || '')}`}>
                              {visit.visit_outcome?.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">Agent: {(visit as any).user_name || 'N/A'}</p>
                          {visit.needs_followup && (
                            <div className="mt-2">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                visit.followup_priority === 'urgent' ? 'bg-fuchsia-700 text-white' :
                                visit.followup_priority === 'high' ? 'bg-red-600 text-white' :
                                visit.followup_priority === 'medium' ? 'bg-orange-600 text-white' :
                                'bg-blue-600 text-white'
                              }`}>
                                FOLLOW-UP: {visit.followup_priority?.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Revisits */}
            {groupedVisits.revisit.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-orange-600 mb-3 flex items-center gap-2">
                  <Clock size={20} />
                  Revisits ({groupedVisits.revisit.length})
                </h3>
                <div className="space-y-2">
                  {groupedVisits.revisit.map((visit) => (
                    <div
                      key={visit.id}
                      onClick={() => handleVisitClick(visit.id)}
                      className="p-4 border border-gray-200 rounded-xl hover:border-orange-400 hover:bg-orange-50 transition-all cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Calendar size={14} className="text-gray-500" />
                            <span className="text-sm font-bold text-gray-900">
                              {formatDate(visit.created_at)}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-800">
                              🔄 REVISIT
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getOutcomeColor(visit.visit_outcome || '')}`}>
                              {visit.visit_outcome?.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">Agent: {(visit as any).user_name || 'N/A'}</p>
                          {visit.needs_followup && (
                            <div className="mt-2">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                visit.followup_priority === 'urgent' ? 'bg-fuchsia-700 text-white' :
                                visit.followup_priority === 'high' ? 'bg-red-600 text-white' :
                                visit.followup_priority === 'medium' ? 'bg-orange-600 text-white' :
                                'bg-blue-600 text-white'
                              }`}>
                                FOLLOW-UP: {visit.followup_priority?.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <PosNotesSection posId={pos.id} />
      </div>

      {/* Visit Detail Modal */}
      <VisitDetailModal
        visit={selectedVisit}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedVisit(null);
        }}
      />

      {/* Schedule Follow-up Modal */}
      {pos && (
        <ScheduleFollowupModal
          isOpen={showScheduleFollowup}
          onClose={() => setShowScheduleFollowup(false)}
          posId={pos.id}
          posName={pos.name}
          storeType={pos.store_type}
          onComplete={() => {
            setShowScheduleFollowup(false);
            checkPendingFollowup();
            fetchPoSVisits(); // Refresh visit list
            toast.success('Follow-up added to To-Do list');
          }}
        />
      )}
    </div>
  );
};

export default PoSDetail;

